import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@exhousing/design-system";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { ContactFieldsEditor } from "@/components/party/ContactFieldsEditor";
import { useUpdateLandlord } from "@/hooks/api/useParties";
import { CONTACT_TEXT_FIELDS, buildContactPayload } from "@/lib/contactFields";
import type { components } from "@/types/api";

type Landlord = components["schemas"]["LandlordResponse"];

const TEXT = [
  { key: "iban", label: "IBAN" },
  { key: "bank_account_name", label: "Account holder" },
  { key: "bank_name", label: "Bank name" },
  { key: "bank_city", label: "Bank city" },
  { key: "brokerage_commission", label: "Brokerage commission (e.g. 5%)" },
  { key: "coc_number", label: "KVK / CoC number" },
] as const;
const DATES = [
  { key: "birth_date", label: "Birth date" },
  { key: "first_contact_at", label: "First contact" },
  { key: "last_contact_at", label: "Last contact" },
] as const;

export function LandlordDetailsCard({ landlord }: { landlord: Landlord }) {
  const update = useUpdateLandlord(landlord.id);
  const [editing, setEditing] = useState(false);
  const c = landlord.contact as Record<string, unknown>;

  const [contact, setContact] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {
      title: (c.title as string) ?? "",
      gender: (c.gender as string) ?? "",
      language: (c.language as string) ?? "nl",
    };
    for (const { key } of CONTACT_TEXT_FIELDS) init[key] = (c[key] as string) ?? "";
    return init;
  });
  const [form, setForm] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {
      landlord_type: landlord.landlord_type,
      category: landlord.category ?? "",
    };
    for (const f of [...TEXT, ...DATES]) {
      const v = landlord[f.key as keyof Landlord];
      init[f.key] = v != null ? String(v) : "";
    }
    return init;
  });
  const [brokerage, setBrokerage] = useState(landlord.has_brokerage_agreement);
  const [thirdParty, setThirdParty] = useState(landlord.is_third_party);

  const setC = (k: string, v: string) => setContact((s) => ({ ...s, [k]: v }));

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    const body: Record<string, unknown> = {
      contact: buildContactPayload(contact, landlord.landlord_type === "company" ? "company" : "person"),
      landlord_type: form.landlord_type,
      category: form.category || null,
      has_brokerage_agreement: brokerage,
      is_third_party: thirdParty,
    };
    for (const f of TEXT) body[f.key] = form[f.key] || null;
    for (const f of DATES) body[f.key] = form[f.key] || null;
    update.mutate(body, {
      onSuccess: () => {
        toast.success("Landlord updated");
        setEditing(false);
      },
      onError: () => toast.error("Could not update landlord"),
    });
  };

  if (editing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Edit landlord</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-5">
            <ContactFieldsEditor form={contact} set={setC} />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="landlord_type">Type</Label>
                <select
                  id="landlord_type"
                  value={form.landlord_type}
                  onChange={(e) => setForm({ ...form, landlord_type: e.target.value })}
                  className="flex h-11 w-full rounded-md border border-input bg-card px-3 text-sm"
                >
                  <option value="private">Private</option>
                  <option value="company">Company</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="category">Category tier</Label>
                <select
                  id="category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="flex h-11 w-full rounded-md border border-input bg-card px-3 text-sm"
                >
                  <option value="">—</option>
                  {["A", "B", "C", "D"].map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              {TEXT.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label htmlFor={f.key}>{f.label}</Label>
                  <Input
                    id={f.key}
                    value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  />
                </div>
              ))}
              {DATES.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label htmlFor={f.key}>{f.label}</Label>
                  <Input
                    id={f.key}
                    type="date"
                    value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  />
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-4">
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={brokerage}
                  onChange={(e) => setBrokerage(e.target.checked)}
                  className="size-4 rounded border-input accent-primary"
                />
                Brokerage agreement
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={thirdParty}
                  onChange={(e) => setThirdParty(e.target.checked)}
                  className="size-4 rounded border-input accent-primary"
                />
                Third party (managing agent, not owner)
              </label>
            </div>
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={update.isPending}>
                {update.isPending ? "Saving…" : "Save landlord"}
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  }

  const row = (label: string, value: unknown) => (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{value ? String(value) : "—"}</dd>
    </div>
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Details</CardTitle>
        <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
          <Pencil className="size-4" />
          Edit
        </Button>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
          {row("Email", landlord.contact.email)}
          {row("Phone", landlord.contact.phone ?? landlord.contact.mobile)}
          {row("IBAN", landlord.iban)}
          {row("Account holder", landlord.bank_account_name)}
          {row("Bank", landlord.bank_name)}
          {row(
            "Brokerage",
            landlord.has_brokerage_agreement
              ? landlord.brokerage_commission || "Yes"
              : "No",
          )}
          {row("Third party", landlord.is_third_party ? "Yes" : "No")}
          {row("KVK", landlord.coc_number)}
          {row("Birth date", landlord.birth_date)}
          {row("First contact", landlord.first_contact_at)}
          {row("Last contact", landlord.last_contact_at)}
        </dl>
      </CardContent>
    </Card>
  );
}
